/**
 * Deadline countdown badge for pre-claim billing surfaces (Notes Ready for
 * Billing, Claims Needing Corrections). Uses the shared `getDeadlineUrgency`
 * helper from /utils/claimDisplay so every deadline pill in the app — this
 * one and the Workshop's claim-level pill — agree on colors and wording.
 *
 * Input modes (in priority order):
 *   1. `daysUntilDeadline` — use this number directly (backend-computed).
 *   2. `explicitDeadlineDate` — compute days from today to this date.
 *   3. `dos` + `filingWindowDays` (default 90) — DOS-based fallback for
 *      notes that don't have a claim yet.
 */
import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { parseLocalDate, formatLocalDate, getDeadlineUrgency } from '../utils/claimDisplay';

const DEFAULT_FILING_WINDOW_DAYS = 90;

const BillingDeadlineBadge = ({
  dos,
  explicitDeadlineDate = null,
  daysUntilDeadline = null,
  filingWindowDays = DEFAULT_FILING_WINDOW_DAYS,
  payerName = null,
}) => {
  const dosDate = parseLocalDate(dos);
  if (!dosDate && daysUntilDeadline === null && !explicitDeadlineDate) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let daysRemaining;
  let deadlineDate;
  if (typeof daysUntilDeadline === 'number') {
    daysRemaining = daysUntilDeadline;
    deadlineDate = explicitDeadlineDate
      ? parseLocalDate(explicitDeadlineDate)
      : new Date(today.getTime() + daysRemaining * 86400000);
  } else if (explicitDeadlineDate) {
    deadlineDate = parseLocalDate(explicitDeadlineDate);
    daysRemaining = deadlineDate ? Math.floor((deadlineDate - today) / 86400000) : null;
  } else {
    deadlineDate = new Date(dosDate.getTime() + filingWindowDays * 86400000);
    daysRemaining = Math.floor((deadlineDate - today) / 86400000);
  }

  const urg = getDeadlineUrgency(daysRemaining);
  const daysSinceDos = dosDate ? Math.floor((today - dosDate) / 86400000) : null;

  const payerSuffix = payerName
    ? ` — ${payerName} allows ${filingWindowDays} days`
    : ` — assumes ${filingWindowDays}-day filing window`;
  const tooltipLines = [
    dos ? `DOS: ${formatLocalDate(dos)}` : null,
    deadlineDate ? `Filing deadline: ${formatLocalDate(deadlineDate.toISOString())}` : null,
    daysSinceDos !== null
      ? `${daysSinceDos} day${daysSinceDos === 1 ? '' : 's'} since DOS${payerSuffix}`
      : null,
  ].filter(Boolean);

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs rounded border ${urg.cls}`}
      title={tooltipLines.join('\n')}
      data-testid="billing-deadline-badge"
    >
      {urg.icon === 'alert' && <AlertTriangle className="h-3 w-3 mr-1" />}
      {urg.icon === 'clock' && <Clock className="h-3 w-3 mr-1" />}
      {urg.label}
    </span>
  );
};

export default BillingDeadlineBadge;
